import { SparklesIcon } from "lucide-react";
import { useEffect, useState } from "react";

const LOADING_PHRASES = [
  "Analyzing your profiles",
  "Reading the visual cues",
  "Finding common ground",
  "Weighing compatibility",
  "Curating your match",
];

// Rounded union outline of the 3 scattered photo cards, traced as one path so a
// single beam can run the full perimeter continuously. The fill uses this path
// directly (edge = path), so a beam stroked centred on it rides the outer border
// rather than sitting inside a fattened shape. ~98 x 73 space; the SVG scales.
const MATCH_OUTLINE =
  "M4 0 L34 0 Q38 0 38 4 L38 18 Q38 22 42 22 L56 22 Q60 22 60 18 L60 12 Q60 8 64 8 L94 8 Q98 8 98 12 L98 54.67 Q98 58.67 94 58.67 L72 58.67 Q68 58.67 68 62.67 L68 68.67 Q68 72.67 64 72.67 L34 72.67 Q30 72.67 30 68.67 L30 54.67 Q30 50.67 26 50.67 L4 50.67 Q0 50.67 0 46.67 L0 4 Q0 0 4 0 Z";

export default function MatchLoading() {
  return (
    <div className="flex flex-col items-center gap-8 pt-6">
      <svg
        viewBox="-4 -4 106 81"
        fill="none"
        className="w-full max-w-2xl mx-auto h-auto"
      >
        <defs>
          <clipPath id="matchUnion">
            <path d={MATCH_OUTLINE} />
          </clipPath>
          <linearGradient id="matchShimmer" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset="0.5" stopColor="#fff" stopOpacity="0.07" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          {/* Both glows live on static circles, so they're rasterised once and
              just composited as animateMotion translates the head — not
              re-blurred per frame like a filter on an animating dash would be. */}
          <filter id="cometGlow" x="-300%" y="-300%" width="700%" height="700%">
            <feGaussianBlur stdDeviation="0.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter
            id="matchHeadShine"
            x="-200%"
            y="-200%"
            width="500%"
            height="500%"
          >
            <feGaussianBlur stdDeviation="1.4" />
          </filter>
        </defs>

        {/* Merged skeleton fill — this rounded path is the outer edge itself. */}
        <path d={MATCH_OUTLINE} fill="#18181b" />

        {/* Shimmer sweep, clipped to the shape. */}
        <g clipPath="url(#matchUnion)">
          <rect x="-45" y="-4" width="45" height="81" fill="url(#matchShimmer)">
            <animate
              attributeName="x"
              from="-45"
              to="100"
              dur="3.12s"
              repeatCount="indefinite"
            />
          </rect>
        </g>

        {/* Faint full-perimeter track the beam runs along. */}
        <path
          d={MATCH_OUTLINE}
          stroke="rgba(255,255,255,0.07)"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />

        {/* Long beam tail — a uniform streak that follows the border's curve via
            stroke-dashoffset. No filter on it (filters on animating dashes were
            the perf cliff); the glow is only on the composited head dot below. */}
        <path
          d={MATCH_OUTLINE}
          stroke="#fff"
          strokeWidth="0.8"
          strokeLinejoin="round"
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray="13 87"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-100"
            dur="6.59s"
            repeatCount="indefinite"
          />
        </path>

        {/* Glowing head with a soft shine at the beam's leading edge, moved by
            animateMotion (GPU-composited; both glows are on static circles, so
            they're rasterised once). begin = -(dash / 100 * dur) phases it onto
            the streak's leading edge. */}
        <g>
          <circle
            r="1.8"
            fill="#fff"
            fillOpacity="0.5"
            filter="url(#matchHeadShine)"
          />
          <circle r="0.4" fill="#fff" filter="url(#cometGlow)" />
          <animateMotion
            path={MATCH_OUTLINE}
            dur="6.59s"
            begin="-0.86s"
            repeatCount="indefinite"
          />
        </g>
      </svg>

      <CyclingPhrase />
    </div>
  );
}

function CyclingPhrase() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((prev) => (prev + 1) % LOADING_PHRASES.length),
      1500,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex items-center gap-2 text-zinc-400">
      <SparklesIcon className="h-4 w-4 animate-pulse text-white" />
      {/* key forces a remount so the fade-in replays on each phrase change */}
      <span key={index} className="animate-fade-in-up text-sm font-light">
        {LOADING_PHRASES[index]}
      </span>
    </div>
  );
}
